import React, { useCallback } from 'react';
import c from 'classnames';
import CustomTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/CustomTooltip';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { Url } from '@wix/da-url';
import { LoggedOutReturnType } from '../../../redux/loggedout';
import { useRestrictToLoggedIn } from '../../../utils/hooks/useRestrictToLoggedIn';
import TooltipSisuContent from '../TooltipSisuContent/TooltipSisuContent';
import { getSisuActionKey } from './actionKey';

import s from './SisuActionPopover.scss';

interface Props {
  restrictedActionName: ReferralType;
  returnTo?: string;
  returnType?: LoggedOutReturnType;
  /**
   * When true, the reference element spans the full width of its parent (e.g. anon comment fake field).
   * Omit for intrinsic-width controls so the tooltip stays centered on the button.
   */
  fullWidthReference?: boolean;
  children: React.ReactNode;
}

const SisuActionPopover: React.FC<Props> = ({
  restrictedActionName,
  returnTo,
  returnType,
  fullWidthReference = false,
  children,
}) => {
  const isSisuPopoverEnabled = useFlag('sisu_popover');
  const { sendUserToJoinPage } = useRestrictToLoggedIn();

  const goToJoinPage = useCallback(() => {
    sendUserToJoinPage(restrictedActionName, returnTo, returnType);
  }, [restrictedActionName, returnTo, returnType, sendUserToJoinPage]);

  const goToLoginPage = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const base = Url.loginLink();
    const sep = base.includes('?') ? '&' : '?';
    window.location.assign(`${base}${sep}referer=${encodeURIComponent(window.location.href)}`);
  }, []);

  if (!isSisuPopoverEnabled) {
    return <>{children}</>;
  }

  return (
    <CustomTooltip
      className={c(s['reference'], fullWidthReference && s['reference-full-width'])}
      placement="top"
      listenOnHover={false}
      relaxedPadding
      usePortal
      content={
        <TooltipSisuContent
          actionKey={getSisuActionKey(restrictedActionName)}
          onLoginClick={goToLoginPage}
          onJoinClick={goToJoinPage}
        />
      }
    >
      {children}
    </CustomTooltip>
  );
};

SisuActionPopover.displayName = 'SisuActionPopover';

export default SisuActionPopover;
